import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SDKProvider, useInitData } from '@telegram-apps/sdk-react';
import { Header } from '@/components/Header';
import { BottomNav } from '@/components/BottomNav';
import { ToastContainer } from '@/components/ToastContainer';
import { HomePage } from '@/pages/HomePage';
import { RafflesPage } from '@/pages/RafflesPage';
import { RaffleDetailPage } from '@/pages/RaffleDetailPage';
import { JackpotPage } from '@/pages/JackpotPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { AdminPage } from '@/pages/AdminPage';
import { WheelPage } from '@/pages/WheelPage';
import { TicketsShopPage } from '@/pages/TicketsShopPage';
import { TransactionsPage } from '@/pages/TransactionsPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { LeaderboardPage } from '@/pages/LeaderboardPage';
import { api } from '@/services/api';
import { useStore } from '@/store/useStore';

const isTelegramApp = typeof window !== 'undefined' &&
  Boolean((window as any).Telegram?.WebApp?.initData);

// Initializes Telegram auth — rendered only inside SDKProvider
function TelegramAuthSync() {
  const initData = useInitData();

  useEffect(() => {
    const tgWebApp = (window as any).Telegram?.WebApp;
    const initDataRaw = initData?.initDataRaw || tgWebApp?.initData;
    if (initDataRaw) {
      api.setInitData(initDataRaw);
    }
  }, [initData]);

  return null;
}

function AppContent() {
  const { fetchUser, user, isWebMode } = useStore();
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    fetchUser().then(() => setInitialized(true));
  }, []);

  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

  // Spinner while first fetch is in progress
  if (!initialized && !isAuthPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-telegram-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-telegram-button" />
      </div>
    );
  }

  // Web mode: redirect to login if not authenticated
  if (initialized && isWebMode && !user && !isAuthPage) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className={`min-h-screen flex flex-col bg-telegram-bg text-telegram-text ${!isAuthPage ? 'pb-20' : ''} safe-area-inset-bottom`}>
      {!isAuthPage && <Header />}
      <main className="flex-1">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/" element={<HomePage />} />
          <Route path="/raffles" element={<RafflesPage />} />
          <Route path="/raffle/:id" element={<RaffleDetailPage />} />
          <Route path="/wheel/:id" element={<WheelPage />} />
          <Route path="/tickets" element={<TicketsShopPage />} />
          <Route path="/jackpot" element={<JackpotPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
        </Routes>
      </main>
      {!isAuthPage && <BottomNav />}
      <ToastContainer />
      {!isAuthPage && (
        <footer
          role="contentinfo"
          aria-label="site-footer"
          className="text-sm text-center text-gray-500 dark:text-gray-400 py-2 relative z-20 mb-16"
        >
          ИП Торопцев Дмитрий Петрович
        </footer>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      {isTelegramApp ? (
        <SDKProvider acceptCustomStyles>
          <TelegramAuthSync />
          <AppContent />
        </SDKProvider>
      ) : (
        <AppContent />
      )}
    </BrowserRouter>
  );
}

export default App;
